import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, Star } from "lucide-react";
import { Hero } from "@/components/sections/Hero";
import { BrandMarquee } from "@/components/BrandMarquee";
import { PackagesGrid } from "@/components/PackagesGrid";
import { FaqAccordion } from "@/components/FaqAccordion";
import { IowaMap } from "@/components/IowaMap";
import { Reveal } from "@/components/Reveal";
import { STEPS, FEATURES, REVIEWS, IMAGES } from "@/lib/data";

const SectionHead = ({ overline, title, desc, light }) => (
  <div className="max-w-2xl mb-14">
    <span className="text-crate-yellow font-bold uppercase tracking-[0.25em] text-xs md:text-sm">{overline}</span>
    <h2 className={`font-heading font-black tracking-tighter text-4xl md:text-6xl mt-4 leading-[0.95] ${light ? "text-crate-black" : "text-white"}`}>{title}</h2>
    {desc && <p className={`mt-5 text-lg leading-relaxed ${light ? "text-crate-black/60" : "text-white/60"}`}>{desc}</p>}
  </div>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <BrandMarquee dark />

      {/* How It Works */}
      <section className="py-24 md:py-32 mx-auto max-w-7xl px-6 md:px-12" data-testid="how-it-works">
        <Reveal><SectionHead overline="How It Works" title="Moving in four simple steps." desc="From click to pickup, we handle the heavy lifting so you can focus on your new home." /></Reveal>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <div className="group relative h-full rounded-2xl bg-crate-surface border border-white/10 p-8 hover:border-crate-yellow transition-colors duration-300">
                <span className="font-heading font-black text-6xl text-white/10 group-hover:text-crate-yellow/30 transition-colors">{s.n}</span>
                <h3 className="font-heading font-bold text-xl text-white mt-4">{s.title}</h3>
                <p className="mt-3 text-white/60 leading-relaxed text-sm">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Why Choose Us — Bento */}
      <section className="py-24 md:py-32 bg-crate-surface/40 border-y border-white/10" data-testid="why-choose-us">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal><SectionHead overline="Why Choose Us" title="Built for stress-free moving." /></Reveal>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f, i) => {
              const Icon = Icons[f.icon] || Icons.Box;
              return (
                <Reveal key={f.title} delay={i * 0.06} className={f.span}>
                  <div className="group h-full rounded-2xl bg-crate-black border border-white/10 p-7 hover:border-crate-yellow hover:-translate-y-1 transition-all duration-300">
                    <span className="grid place-items-center h-12 w-12 rounded-xl bg-crate-yellow/10 text-crate-yellow group-hover:bg-crate-yellow group-hover:text-crate-black transition-colors">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="font-heading font-bold text-lg text-white mt-5">{f.title}</h3>
                    <p className="mt-2 text-white/55 text-sm leading-relaxed">{f.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Rental Packages */}
      <section className="py-24 md:py-32 mx-auto max-w-7xl px-6 md:px-12" data-testid="rental-packages">
        <Reveal><SectionHead overline="Rental Packages" title="Simple, all-inclusive pricing." desc="Every package includes free delivery and pickup. No hidden fees, no deposits." /></Reveal>
        <PackagesGrid />
      </section>

      {/* Service Areas */}
      <section className="py-24 md:py-32 bg-crate-surface/40 border-y border-white/10" data-testid="service-areas">
        <div className="mx-auto max-w-7xl px-6 md:px-12 grid gap-12 lg:grid-cols-2 items-center">
          <Reveal>
            <SectionHead overline="Service Areas" title="Proudly serving Southwest Iowa." desc="We deliver reusable moving totes across these communities — with more towns on the way." />
            <div className="flex flex-wrap gap-3">
              <Link to="/reserve" className="inline-flex items-center gap-2 rounded-full bg-crate-yellow text-crate-black font-bold px-7 py-3.5 hover:bg-white transition-colors" data-testid="areas-reserve-btn">
                Check Availability <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}><IowaMap /></Reveal>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-24 md:py-32 mx-auto max-w-7xl px-6 md:px-12" data-testid="reviews">
        <Reveal><SectionHead overline="Customer Reviews" title="Loved by movers across Iowa." /></Reveal>
        <div className="flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory -mx-6 px-6 md:mx-0 md:px-0 scrollbar-hide">
          {REVIEWS.map((r, i) => (
            <Reveal key={i} delay={i * 0.05} className="snap-start shrink-0 w-[85%] sm:w-[380px]">
              <figure className="h-full rounded-2xl bg-crate-surface border border-white/10 p-8 flex flex-col">
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: r.stars }).map((_, s) => <Star key={s} className="h-5 w-5 fill-crate-yellow text-crate-yellow" />)}
                </div>
                <blockquote className="text-white/80 leading-relaxed flex-1">"{r.text}"</blockquote>
                <figcaption className="mt-6 pt-5 border-t border-white/10">
                  <p className="font-heading font-bold text-white">{r.name}</p>
                  <p className="text-crate-yellow text-sm">{r.role}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32 bg-crate-surface/40 border-y border-white/10" data-testid="faq-section">
        <div className="mx-auto max-w-3xl px-6 md:px-12">
          <Reveal><SectionHead overline="FAQ" title="Questions, answered." /></Reveal>
          <Reveal delay={0.1}><FaqAccordion /></Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-crate-yellow py-20 md:py-28 grain" data-testid="final-cta">
        <div className="mx-auto max-w-7xl px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 relative z-10">
          <div>
            <h2 className="font-heading font-black text-crate-black tracking-tighter text-5xl md:text-7xl leading-[0.9]">Ready to<br />Move Smarter?</h2>
            <p className="mt-5 text-crate-black/70 text-lg max-w-md">Reserve your reusable moving totes today and pack by color.</p>
          </div>
          <Link to="/reserve" data-testid="cta-reserve-btn" className="group inline-flex items-center gap-3 rounded-full bg-crate-black text-white font-bold px-10 py-5 text-lg hover:bg-white hover:text-crate-black transition-colors shrink-0">
            Reserve Now <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
